import { getCokkie, getrefreshCokkie, logout } from "../views/login/Login";

const REFRESH_COOLDOWN_MS = 1500;
const MAX_RETRIES = 5;

let refreshPromise = null;
let lastRefreshAt = 0;

async function refreshAccessTokenSingleFlight() {
  const now = Date.now();

  if (refreshPromise) return refreshPromise;

  if (now - lastRefreshAt < REFRESH_COOLDOWN_MS) {
    return Promise.resolve(getCokkie("jwtToken"));
  }

  lastRefreshAt = now;

  refreshPromise = (async () => {
    const refresh_token = getrefreshCokkie("refreshToken");

    const res = await fetch(`${window.ENV.API_URL}/authentication/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token }),
    });

    if (!res.ok) throw new Error("Refresh token expired");

    const data = await res.json();

    const newAccessToken = data.access_token ?? data.accessToken;
    const newRefreshToken = data.refresh_token ?? data.refreshToken;

    if (!newAccessToken) {
      throw new Error("Refresh response missing access token");
    }

    document.cookie = `jwtToken=${newAccessToken}; path=/`;
    if (newRefreshToken) {
      document.cookie = `refreshToken=${newRefreshToken}; path=/`;
    }

    return newAccessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

function shouldRetryHttpStatus(status) {
  return status === 504 || status === 503;
}

function isRetryableFetchError(error) {
  // Never retry intentional aborts — useFetch aborts on unmount/dep change.
  if (error?.name === "AbortError") {
    return false;
  }

  const message = String(error?.message || "").toLowerCase();

  return (
    message.includes("network error") ||
    message.includes("failed to fetch") ||
    message.includes("load failed") ||
    message.includes("the network connection was lost") ||
    message.includes("fetch")
  );
}

function getErrorMessageFromBody(bodyJson) {
  const detail = bodyJson?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const detailMessages = detail
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.message || item?.msg
      )
      .filter(Boolean);

    if (detailMessages.length) return detailMessages.join(", ");
  }

  if (detail?.message) return detail.message;
  if (bodyJson?.message) return bodyJson.message;

  return "";
}

async function doFetchWithRetry(url, requestOptions, maxRetries = MAX_RETRIES) {
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, requestOptions);

      if (shouldRetryHttpStatus(response.status) && attempt < maxRetries) {
        lastError = {
          status: response.status,
          type: "RETRYABLE_HTTP_ERROR",
          message: `Retryable HTTP error: ${response.status}`,
        };
        continue;
      }

      return response;
    } catch (e) {
      // Aborted requests must not be retried (same aborted signal would spam canceled calls).
      if (
        e?.name === "AbortError" ||
        requestOptions?.signal?.aborted
      ) {
        throw e;
      }

      if (isRetryableFetchError(e) && attempt < maxRetries) {
        lastError = e;
        continue;
      }

      throw {
        status: 0,
        type: "NETWORK",
        message: e?.message || "Network error",
        cause: e,
      };
    }
  }

  if (lastError?.status) {
    throw lastError;
  }

  throw {
    status: 0,
    type: "NETWORK",
    message: lastError?.message || "Network error after retries",
    cause: lastError,
  };
}

export default async function getDataFromApi(
  url,
  method = "GET",
  body,
  type,
  signal,
  retry = false
) {
  const token = getCokkie("jwtToken");

  const headers = new Headers();
  headers.append("accept", "application/json");
  headers.append("Authorization", `Bearer ${token}`);
  headers.append("Cache-Control", "no-cache");

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (body && !isFormData) {
    headers.append("Content-Type", "application/json");
  }

  const requestOptions = {
    method,
    headers,
    signal,
    ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
  };

  let response;

  try {
    const shouldRetryRequest =
      retry || String(method).toUpperCase() === "GET";

    response = shouldRetryRequest
      ? await doFetchWithRetry(url, requestOptions, MAX_RETRIES)
      : await fetch(url, requestOptions);
  } catch (e) {
    if (e?.name === "AbortError" || requestOptions?.signal?.aborted) {
      throw e;
    }

    if (e?.status) throw e;

    throw {
      status: 0,
      type: "NETWORK",
      message: e?.message || "Network error",
      cause: e,
    };
  }

  if (response.status < 400) {
    if (type === "blob") return response.blob();
    if (type === "blobWithMetadata") {
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^;"]+)/i);
      return {
        blob,
        filename: filenameMatch ? decodeURIComponent(filenameMatch[1].trim()) : "guidelines",
        mediaType: response.headers.get("content-type") || blob.type,
      };
    }
    if (type === "text") return response.text();
    return response.json();
  }

  let bodyJson = null;
  try {
    bodyJson = await response.json();
  } catch { }

  const responseMessage = getErrorMessageFromBody(bodyJson);

  if (response.status === 403) {
    throw {
      status: 403,
      type: "FORBIDDEN",
      message: responseMessage || "🚫 You are unauthorised to use this action",
      body: bodyJson,
    };
  }

  if (response.status === 401) {
    if (retry) {
      logout();
      throw {
        status: 401,
        type: "UNAUTHORIZED",
        message: "User not Authenticated",
      };
    }

    try {
      await refreshAccessTokenSingleFlight();
      return getDataFromApi(url, method, body, type, signal, true);
    } catch (err) {
      logout();
      throw {
        status: 401,
        type: "UNAUTHORIZED",
        message: "User not Authenticated",
        cause: err,
      };
    }
  }

  throw {
    status: response.status,
    type: response.status >= 500 ? "SERVER_ERROR" : "CLIENT_ERROR",
    message: responseMessage || `API Error: ${response.status}`,
    body: bodyJson,
  };
}
